import { isFindexFile } from '@/types/file'
import { dateIsValid } from '../formatting/date'
import { toNumber } from '../number'
import * as fns from 'date-fns'
import { isFile } from './file'
import { isAttachment, isDocRef, isSavedAttachment, isValue } from 'findef'
import { isVector } from 'math'
import type { FunctionalComponent } from 'vue'
import { isDBObject } from '@/types/dbObject'

export const isFunction = (v?: any): v is CallableFunction => {
  if (!v) return false
  return typeof v === 'function'
}

export const isFunctionalComponent = (x: any): x is FunctionalComponent => {
  return isFunction(x);
}

export const isArray = <T = any>(x: any): x is T[] => {
  if (isUndefined(x) || x === null) return false
  return Array.isArray(x)
}

export const isNumberArray = (x: any): x is Array<number> => {
  if (!Array.isArray(x)) return false
  return x.filter((it) => isNumber(it)).length >= x.length
}

export const isNumber = (x: any): x is number => typeof x === 'number'
export const isSafeNumber = (x: any): x is number => {
  if (!isNumber(x)) return false;
  return isFinite(x) && !isNaN(x);
}
export const isString = (x: any): x is string => typeof x === 'string'
export const isBoolean = (x: any): x is boolean => typeof x === 'boolean'
export const isFactor = (x: any): x is number | string | boolean | Date =>
  isVector(x) ||
  isNumber(x) ||
  isString(x) ||
  isBoolean(x) ||
  isDate(x) ||
  isFindexFile(x) ||
  isFile(x) ||
  isAttachment(x) ||
  isSavedAttachment(x) ||
  isDocRef(x) ||
  isNumberArray(x) ||
  isValue(x) ||
  isDBObject(x)
export const isUndefined = (x?: any): x is undefined => typeof x === 'undefined'

export const isDigit = (c: string): boolean => {
  if (isUndefined(c) || c === null) return false
  const n = c.codePointAt(0) || 0
  return n >= 48 && n <= 57
}

export const isAlpha = (c: string): boolean => {
  const code = c.codePointAt(0)
  if (!code) return false
  return code >= 65 && code <= 122
}

export const isNumerical = (c: string): boolean => {
  const digits = Array.from(c).filter(isDigit)
  return digits.length >= c.length
}

export const isNumericalString = (str: any): boolean => {
  if (typeof str !== 'string') {
    return false;
  }

  const trimmedStr = str.trim();
  if (trimmedStr.length === 0) {
    return false;
  }

  const num = Number(trimmedStr);
  return !isNaN(num) && isFinite(num);
}


export const extractNumber = (v: string): number => {
  return toNumber(
    Array.from(v.trim())
      .filter((s) => isDigit(s) || s === '.')
      .join('')
  )
}

export const extractNumbers = (v: string, delim: string = ' '): number[] => {
  if (v.includes(delim) === false) return [extractNumber(v)]
  return v
    .split(delim)
    .map(extractNumber)
    .filter((v) => isInf(v) === false && isNaN(v) === false)
}

export const isFloat = (x: any): x is number => isNumber(x) && x.toString().includes('.')

export const isHTMLElement = (x: any): x is HTMLElement => {
  if (!x) return false
  return !!x.appendChild
}

export const isSVGElement = (x: any): x is SVGElement => {
  if (!x) return false
  if (typeof x !== 'object') return false;
  if (typeof x.tagName !== 'string') return false;
  return x.tagName === 'svg';
}

export const isHTMLString = (x: any): x is string => {
  if (!x) return false
  if (!isString(x)) return false

  return x.includes('<') && x.includes('>')
}

export const isDate = (x: any): x is Date => {
  if (!x) return false
  if (typeof x !== 'object') return false
  return !!x.getDay
}

export const isDateString = (x: any): x is string => {
  if (!x) return false
  if (typeof x !== 'string') return false
  if (fns.isDate(x)) return true
  try {
    const date = new Date(x)
    if (dateIsValid(date)) return true
  } catch (e) {
    return false
  }
  return false
}

export const isSvgTag = (name: string): name is string =>
  ['svg', 'path', 'line', 'circle', 'g', 'clipPath', 'mask', 'view'].includes(name)

export const isPromise = <T = any>(x: any): x is Promise<T> => {
  if (!x) return false
  return !!x.then || !!x.catch
}

export const isInf = (x: number): boolean => !isFinite(x)

export const isError = (x: any): x is Error => {
  if (!x) return false
  if (typeof x !== 'object') return false
  return !!x.stack
}

export const isNone = (x: any): x is undefined | null => {
  if (x === 0) return false;
  if (isUndefined(x)) return true;
  if (x === null) return true;
  return false;
}

export const isNullish = (x: any): x is undefined | null => {
  if (x === 0) return false;
  if (isUndefined(x)) return true
  if (x === null) return true
  if (isString(x) && x.trim().length <= 0) return true
  return false
}

export const isNotNullish = (x: any): x is Exclude<typeof x, null | undefined> => !isNullish(x)

export const isEmail = (email: string): boolean => {
  const regex =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  return isString(email) && regex.test(String(email).toLowerCase())
}

export const isSVGGeometryElement = (x: any): x is SVGGeometryElement => {
  if (!x) return false
  if (typeof x !== 'object') return false
  return typeof x.getPointAtLength === 'function'
}
export type AnyAsyncFunction<T = any> = (...args: any[]) => Promise<T>;
export const isAsyncFunction = <T = any>(x: any): x is AnyAsyncFunction<T> => {
  if (!x) return false;
  if (typeof x !== 'function') return false;
  if (typeof x.constructor !== 'function') return false;
  return x.constructor.name === 'AsyncFunction';
}
